import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { useEffect, useLayoutEffect, useMemo, useState } from "react"
import "react-image-crop/dist/ReactCrop.css"
import { useDispatch, useSelector } from "react-redux"
import { useSearchParams } from "react-router-dom"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import CloseableSnackbar, {
  useCloseableSnackbarState,
} from "../../../common/components/CloseableSnackbar"
import Column, { PaddedContentColumn } from "../../../common/components/Column"
import ConditionalPurpleLinearProgress from "../../../common/components/ConditionalLinearProgress"
import PageTopSection from "../../../common/components/PageTopSection"
import Row, { RowButColumnOnMobile } from "../../../common/components/Row"
import TabContent from "../../../common/components/TabContent"
import UserAvatar from "../../../common/components/UserAvatar"
import SegmentTracker from "../../../common/tracking"
import {
  setErrorFromAPIArray,
  useNavigateUpdatedSearchParams,
} from "../../../common/utils"
import { isAccountOwner } from "../../users/accountRole"
import {
  selectProfile,
  selectProfileLoaded,
  updateProfile,
} from "../../users/reducer"
import ConciergePanel from "../concierge/ConciergePanel"
import { LegacySettingsRedirect } from "../redirects"
import { useValidatedPhoneNumber } from "../utils"
import AcuityCalendarTab from "./AcuityCalendarTab"
import AiProfileFromWebsiteCard from "./AiProfileFromWebsiteCard"
import AvatarEditor, { useAvatarEditorState } from "./AvatarEditor"
import CampaignTab from "./CampaignTab"
import ConfigureEmailTab from "./ConfigureEmailTab"
import OfficeTab from "./OfficeTab"
import PhoneSmsConnectionTab from "./PhoneSmsConnectionTab"
import PlanBillingTab from "./PlanBillingTab"
import ProfileColumn from "./ProfileColumn"
import SessionTab from "./SessionTab"
import SettingsSidebarNav, {
  SETTINGS_TAB_META,
  SettingsBreadcrumbs,
  buildSettingsNavGroups,
} from "./SettingsSidebarNav"
import TeamTab from "./TeamTab"
import { getVisibleSettingsTabs } from "./settingsTabAccess"
import {
  PLAN_BILLING_SECTIONS,
  SETTINGS_TABS,
  createPlanBillingURL,
  createSettingsTabURL,
} from "./settingsTabs"

export {
  PLAN_BILLING_SECTIONS,
  SETTINGS_TABS,
  createPlanBillingURL,
  createSettingsTabURL,
}

export default function Settings() {
  return (
    <LegacySettingsRedirect>
      <SettingsPage />
    </LegacySettingsRedirect>
  )
}

function SettingsPage() {
  const ACTIVE_TAB_SEARCH_PARAM = "tab"
  const [searchParams] = useSearchParams()
  const navigateUpdatedSearchParams = useNavigateUpdatedSearchParams()
  const tabFromUrl =
    searchParams.get(ACTIVE_TAB_SEARCH_PARAM) || SETTINGS_TABS.profile
  const profile = useSelector(selectProfile)
  const profileLoaded = useSelector(selectProfileLoaded)
  const accountIsOwner = isAccountOwner(profile)
  const visibleTabs = useMemo(() => getVisibleSettingsTabs(profile), [profile])
  const visibleTabSet = useMemo(
    () => new Set(visibleTabs.map(({ tab }) => tab)),
    [visibleTabs]
  )
  const tabIsAllowed = visibleTabSet.has(tabFromUrl)
  const activeTab = tabIsAllowed ? tabFromUrl : SETTINGS_TABS.profile
  const [firstName, setFirstName] = useState("")
  const [firstNameError, setFirstNameError] = useState(null)
  const [lastName, setLastName] = useState("")
  const [lastNameError, setLastNameError] = useState(null)
  const { phone, phoneError, handlePhoneChanged } = useValidatedPhoneNumber()
  const [city, setCity] = useState("")
  const [state, setState] = useState("")
  const [profileDataUpdated, setProfileDataUpdated] = useState(false)
  const {
    inputElemRef: profileImageInputElemRef,
    handleNewImageSelected: handleNewProfileImageSelected,
    handleEditButtonClicked: handleUpdateProfileImageButtonClicked,
    handleCropFinished,
    handleCropPopoverClosed,
    croppedImageBlob: croppedProfileImageBlob,
    imageName: profileImageName,
    cropPopoverOpen,
    croppedImageDataURL,
    setCroppedImageDataURL,
    imageDataURL,
    popoverAnchorElemRef,
  } = useAvatarEditorState()
  const [isLoading, setIsLoading] = useState(false)
  const [isStripeFormLoading, setIsStripeFormLoading] = useState(false)
  const {
    isOpen: successSnackbarOpen,
    message: successSnackbarMessage,
    severity: snackbarSeverity,
    open: openSuccessSnackbar,
    close: closeSuccessSnackbar,
  } = useCloseableSnackbarState()
  const dispatch = useDispatch()
  const axios = useAuthorizedAxiosClient()

  const navGroups = useMemo(
    () => buildSettingsNavGroups(visibleTabs),
    [visibleTabs]
  )

  const activeTabMeta = SETTINGS_TAB_META[activeTab]

  useLayoutEffect(() => {
    // Wait for the profile: before it loads every role-gated tab looks hidden.
    if (profileLoaded && !tabIsAllowed) {
      navigateUpdatedSearchParams({
        [ACTIVE_TAB_SEARCH_PARAM]: SETTINGS_TABS.profile,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- redirect only when tab value is disallowed
  }, [tabFromUrl, tabIsAllowed, profileLoaded])

  function handleTabChanged(newValue) {
    navigateUpdatedSearchParams({ [ACTIVE_TAB_SEARCH_PARAM]: newValue })
  }

  function handleGoToProfile() {
    navigateUpdatedSearchParams({
      [ACTIVE_TAB_SEARCH_PARAM]: SETTINGS_TABS.profile,
    })
  }

  function handleFieldUpdatedWrapper(setFieldValue, setError) {
    return function (e) {
      setProfileDataUpdated(true)
      setFieldValue(e.target.value)
      if (setError) {
        setError(null)
      }
    }
  }

  function handlePhoneChanged2(e) {
    setProfileDataUpdated(true)
    handlePhoneChanged(e.target.value)
  }

  function goToCampaignTab() {
    navigateUpdatedSearchParams({
      [ACTIVE_TAB_SEARCH_PARAM]: SETTINGS_TABS.campaign,
    })
  }

  async function handleAiProfileSaved() {
    openSuccessSnackbar(
      "Your campaign profile has been submitted and synced to Airtable."
    )
    goToCampaignTab()
  }

  function handleAiProfileDraftSaved() {
    openSuccessSnackbar("Your campaign profile has been saved.")
  }

  function handleSaveProfileButtonClicked() {
    setIsLoading(true)
    const formData = new FormData()
    formData.append("first_name", firstName)
    formData.append("last_name", lastName)
    formData.append("phone", phone)
    formData.append("city", city)
    formData.append("state", state)
    if (croppedProfileImageBlob) {
      formData.append(
        "profile_image",
        croppedProfileImageBlob,
        profileImageName
      )
    }
    axios
      .put("/clients/api/profile/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then(({ data }) => {
        dispatch(updateProfile(data))
        openSuccessSnackbar("Profile updated")
        SegmentTracker.updateProfile(data)
      })
      .catch(({ response: { data } }) => {
        setErrorFromAPIArray(data, "first_name", setFirstNameError)
        setErrorFromAPIArray(data, "last_name", setLastNameError)
      })
      .finally(() => {
        setIsLoading(false)
        setProfileDataUpdated(false)
      })
  }

  useEffect(
    () => {
      if (profileLoaded) {
        setFirstName(profile.first_name || "")
        setLastName(profile.last_name || "")
        handlePhoneChanged(profile.phone || "")
        setCity(profile.city || "")
        setState(profile.state || "")
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [profile, profileLoaded]
  )

  function handleCancelProfileEditsClicked() {
    setFirstName(profile.first_name || "")
    setFirstNameError(null)
    setLastName(profile.last_name || "")
    setLastNameError(null)
    handlePhoneChanged(profile.phone || "")
    setCity(profile.city || "")
    setState(profile.state || "")
    setCroppedImageDataURL("")
    setProfileDataUpdated(false)
  }

  return (
    <Column>
      <PageTopSection title="Settings" />
      <ConditionalPurpleLinearProgress
        visible={isLoading || isStripeFormLoading}
      />
      <PaddedContentColumn pb={4}>
        <RowButColumnOnMobile sx={{ gap: 3, alignItems: "flex-start" }}>
          <SettingsSidebarNav
            groups={navGroups}
            activeTab={activeTab}
            onSelect={handleTabChanged}
          />
          <Column sx={{ flex: 1, minWidth: 0, gap: 2.5 }}>
            <SettingsBreadcrumbs
              groupLabel={activeTabMeta?.group}
              pageLabel={activeTabMeta?.label}
              onGoHome={handleGoToProfile}
            />
            <Box>
              <Typography variant="h2">{activeTabMeta?.label}</Typography>
              {activeTabMeta?.description && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  {activeTabMeta.description}
                </Typography>
              )}
            </Box>
            <TabContent value={activeTab} index={SETTINGS_TABS.profile}>
              <ProfileColumn gap={2.5}>
                <Box
                  sx={{
                    bgcolor: "#fff",
                    border: "1px solid",
                    borderColor: "colors.gray200",
                    borderRadius: "16px",
                    p: { xs: 2, sm: 3 },
                  }}
                >
                  <Typography variant="h5" sx={{ mb: 2.5 }}>
                    Personal information
                  </Typography>
                  <RowButColumnOnMobile gap={3}>
                    <Column
                      sx={{
                        alignItems: "center",
                        gap: 1.5,
                        width: { xs: "100%", sm: "128px" },
                        flexShrink: 0,
                      }}
                    >
                      <AvatarEditor
                        cropPopoverOpen={cropPopoverOpen}
                        croppedImageDataURL={croppedImageDataURL}
                        handleCropFinished={handleCropFinished}
                        handleCropPopoverClosed={handleCropPopoverClosed}
                        imageDataURL={imageDataURL}
                        inputElemRef={profileImageInputElemRef}
                        onEditButtonClicked={
                          handleUpdateProfileImageButtonClicked
                        }
                        onNewImageSelected={handleNewProfileImageSelected}
                        popoverAnchorElemRef={popoverAnchorElemRef}
                      >
                        <UserAvatar width="96px" fontSize="2.75em" />
                      </AvatarEditor>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleUpdateProfileImageButtonClicked}
                      >
                        Change photo
                      </Button>
                    </Column>
                    <Column sx={{ gap: 2, width: "100%" }}>
                      <RowButColumnOnMobile gap={2}>
                        <TextField
                          label="First Name"
                          size="small"
                          value={firstName}
                          onChange={handleFieldUpdatedWrapper(
                            setFirstName,
                            setFirstNameError
                          )}
                          error={!!firstNameError}
                          helperText={firstNameError}
                          required
                          fullWidth
                        />
                        <TextField
                          label="Last Name"
                          size="small"
                          value={lastName}
                          onChange={handleFieldUpdatedWrapper(
                            setLastName,
                            setLastNameError
                          )}
                          error={!!lastNameError}
                          helperText={lastNameError}
                          required
                          fullWidth
                        />
                      </RowButColumnOnMobile>
                      <RowButColumnOnMobile gap={2}>
                        <TextField
                          label="Email"
                          size="small"
                          type="email"
                          disabled
                          value={profile.email}
                          fullWidth
                        />
                        <TextField
                          label="Phone Number"
                          size="small"
                          value={phone}
                          onChange={handlePhoneChanged2}
                          error={!!phoneError}
                          helperText={phoneError}
                          type="tel"
                          fullWidth
                        />
                      </RowButColumnOnMobile>
                      <RowButColumnOnMobile gap={2}>
                        <TextField
                          label="City"
                          size="small"
                          value={city}
                          onChange={handleFieldUpdatedWrapper(setCity)}
                          fullWidth
                        />
                        <TextField
                          label="State"
                          size="small"
                          value={state}
                          onChange={handleFieldUpdatedWrapper(setState)}
                          fullWidth
                        />
                      </RowButColumnOnMobile>
                    </Column>
                  </RowButColumnOnMobile>
                  <Row
                    sx={{
                      justifyContent: "flex-end",
                      gap: 1,
                      mt: 3,
                      pt: 2,
                      borderTop: "1px solid",
                      borderColor: "colors.gray200",
                    }}
                  >
                    <Button
                      variant="outlined"
                      onClick={handleCancelProfileEditsClicked}
                      disabled={!croppedProfileImageBlob && !profileDataUpdated}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleSaveProfileButtonClicked}
                      disabled={
                        (!croppedProfileImageBlob && !profileDataUpdated) ||
                        firstNameError ||
                        lastNameError ||
                        phoneError
                      }
                    >
                      Save changes
                    </Button>
                  </Row>
                </Box>
                {accountIsOwner && (
                  <AiProfileFromWebsiteCard
                    onProfileSaved={handleAiProfileSaved}
                    onProfileDraftSaved={handleAiProfileDraftSaved}
                    onViewCampaign={goToCampaignTab}
                  />
                )}
              </ProfileColumn>
            </TabContent>
            {visibleTabSet.has(SETTINGS_TABS.campaign) && (
              <TabContent value={activeTab} index={SETTINGS_TABS.campaign}>
                <CampaignTab />
              </TabContent>
            )}
            {visibleTabSet.has(SETTINGS_TABS.session) && (
              <TabContent value={activeTab} index={SETTINGS_TABS.session}>
                <SessionTab />
              </TabContent>
            )}
            {visibleTabSet.has(SETTINGS_TABS.team) && (
              <TabContent value={activeTab} index={SETTINGS_TABS.team}>
                <TeamTab />
              </TabContent>
            )}
            {visibleTabSet.has(SETTINGS_TABS.concierge) && (
              <TabContent value={activeTab} index={SETTINGS_TABS.concierge}>
                <ConciergePanel />
              </TabContent>
            )}
            {visibleTabSet.has(SETTINGS_TABS.crmSettings) && (
              <TabContent value={activeTab} index={SETTINGS_TABS.crmSettings}>
                <OfficeTab />
              </TabContent>
            )}
            {visibleTabSet.has(SETTINGS_TABS.phoneSms) && (
              <TabContent value={activeTab} index={SETTINGS_TABS.phoneSms}>
                <PhoneSmsConnectionTab />
              </TabContent>
            )}
            {visibleTabSet.has(SETTINGS_TABS.configureEmail) && (
              <TabContent
                value={activeTab}
                index={SETTINGS_TABS.configureEmail}
              >
                <ConfigureEmailTab onStatusMessage={openSuccessSnackbar} />
              </TabContent>
            )}
            {visibleTabSet.has(SETTINGS_TABS.acuityCalendar) && (
              <TabContent
                value={activeTab}
                index={SETTINGS_TABS.acuityCalendar}
              >
                <AcuityCalendarTab />
              </TabContent>
            )}
            {visibleTabSet.has(SETTINGS_TABS.planBilling) && (
              <TabContent value={activeTab} index={SETTINGS_TABS.planBilling}>
                <PlanBillingTab
                  onStripeFormLoadingChanged={setIsStripeFormLoading}
                />
              </TabContent>
            )}
          </Column>
        </RowButColumnOnMobile>
        <CloseableSnackbar
          open={successSnackbarOpen}
          onClose={closeSuccessSnackbar}
          message={successSnackbarMessage}
          severity={snackbarSeverity}
        />
      </PaddedContentColumn>
    </Column>
  )
}
